import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Heading,
    Button,
    HStack,
    Input,
    VStack,
    Select,
    Flex,
    useBreakpointValue,
    useToast
} from '@chakra-ui/react';
import { useEvents } from '../hooks/useEvents';
import { useTimezone } from '../contexts/TimezoneContext';
import { groupEventsByDate } from '../utils/eventUtils';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { EventsTable } from '../components/events/EventsTable';
import { apiService, GlobalEvent } from '../services/api';
import { ruleSetLabel } from '../utils/ruleSet';

export const EventsPage: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [selectedSport, setSelectedSport] = useState<string>('');
    const [selectedRuleSet, setSelectedRuleSet] = useState<string>('');
    const [selectedTournament, setSelectedTournament] = useState<string>('');
    const [selectedTeamOne, setSelectedTeamOne] = useState<string>('');
    const [selectedTeamTwo, setSelectedTeamTwo] = useState<string>('');
    const [timeZone] = useTimezone();
    const navigate = useNavigate();
    const toast = useToast();
    const [isRecoveringHealth, setIsRecoveringHealth] = useState(false);
    
    // Use our custom hook to manage events data and operations
    const { events, isLoading, cancelEvent } = useEvents(selectedDate);

    // Distinct sport / tournament / team values from the loaded events (for client-side filters)
    const sportOptions = useMemo(
        () => Array.from(new Set(events.map(e => e.sport?.name).filter(Boolean) as string[])).sort(),
        [events]
    );
    // Rule sets available for the selected sport (all sports when none is selected)
    const ruleSetOptions = useMemo(
        () => Array.from(new Set(
            events
                .filter(e => !selectedSport || e.sport?.name === selectedSport)
                .map(e => e.competition?.ruleSet)
                .filter(Boolean) as string[]
        )).sort(),
        [events, selectedSport]
    );
    const tournamentOptions = useMemo(
        () => Array.from(new Set(events.map(e => e.competition?.name).filter(Boolean) as string[])).sort(),
        [events]
    );
    const teamOneOptions = useMemo(
        () => Array.from(new Set(events.map(e => e.firstCompetitor?.name).filter(Boolean) as string[])).sort(),
        [events]
    );
    const teamTwoOptions = useMemo(
        () => Array.from(new Set(events.map(e => e.secondCompetitor?.name).filter(Boolean) as string[])).sort(),
        [events]
    );

    // Client-side filtering by tournament and each team side
    const filteredEvents = useMemo(
        () => events.filter(e => {
            if (selectedSport && e.sport?.name !== selectedSport) return false;
            if (selectedTournament && e.competition?.name !== selectedTournament) return false;
            if (selectedTeamOne && e.firstCompetitor?.name !== selectedTeamOne) return false;
            if (selectedTeamTwo && e.secondCompetitor?.name !== selectedTeamTwo) return false;
            return true;
        }),
        [events, selectedSport, selectedTournament, selectedTeamOne, selectedTeamTwo]
    );

    // Group events by date (in the selected timezone) and filter by rule set
    const groupedEvents = groupEventsByDate(filteredEvents, selectedRuleSet, timeZone);
    
    // Use responsive values for UI elements
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Handle muting and unmuting alerts
    const handleMuteAlerts = async () => {
        try {
            const event: GlobalEvent = { command: 'SLACK_MUTED' };
            await apiService.sendGlobalEvent(event);
            toast({
                title: 'Alerts muted',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to mute alerts',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUnmuteAlerts = async () => {
        try {
            const event: GlobalEvent = { command: 'SLACK_UNMUTED' };
            await apiService.sendGlobalEvent(event);
            toast({
                title: 'Alerts unmuted',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to unmute alerts',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Handle system close and recovery
    const handleCloseSystem = async () => {
        try {
            const event: GlobalEvent = { command: 'SYSTEM_DOWN' };
            await apiService.sendGlobalEvent(event);
            toast({
                title: 'System closed',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to close system',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleRecoverSystem = async () => {
        try {
            const event: GlobalEvent = { command: 'SYSTEM_RECOVERED' };
            await apiService.sendGlobalEvent(event);
            toast({
                title: 'System recovered',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to recover system',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Clears health-driven suspensions on upcoming events (by_health_state false -> true).
    const handleRecoverHealth = async () => {
        setIsRecoveringHealth(true);
        try {
            const { updated } = await apiService.recoverMarketsHealth();
            toast({
                title: 'Health recovered',
                description: `${updated} market${updated === 1 ? '' : 's'} restored on upcoming events`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to recover health',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsRecoveringHealth(false);
        }
    };
    
    return (
        <Box p={{ base: 2, md: 4 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Flex 
                    direction={{ base: 'column', sm: 'row' }} 
                    justify="space-between"
                    align={{ base: 'flex-start', sm: 'center' }}
                    gap={{ base: 2, sm: 0 }}
                >
                    <Heading size={headingSize}>Events</Heading>
                    <HStack spacing={2}>
                        <Button size={buttonSize} onClick={handleMuteAlerts} colorScheme="orange">
                            Mute alerts
                        </Button>
                        <Button size={buttonSize} onClick={handleUnmuteAlerts} colorScheme="green">
                            Unmute alerts
                        </Button>
                        <Button size={buttonSize} onClick={handleCloseSystem} colorScheme="red">
                            Close system
                        </Button>
                        <Button size={buttonSize} onClick={handleRecoverSystem} colorScheme="blue">
                            Recover system
                        </Button>
                        <Button
                            size={buttonSize}
                            onClick={handleRecoverHealth}
                            colorScheme="teal"
                            isLoading={isRecoveringHealth}
                            loadingText="Recovering"
                        >
                            Recover health
                        </Button>
                        <Button size={buttonSize} onClick={() => navigate('/')} colorScheme="red" variant="outline">
                            Logout
                        </Button>
                    </HStack>
                </Flex>

                <Box mb={{ base: 3, md: 4 }}>
                    <Flex 
                        direction={{ base: 'column', md: 'row' }} 
                        gap={{ base: 2, md: 4 }}
                        align={{ base: 'stretch', md: 'center' }}
                    >
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                        />
                        <Select
                            value={selectedSport}
                            onChange={(e) => { setSelectedSport(e.target.value); setSelectedRuleSet(''); }}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '150px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All sports"
                        >
                            {sportOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                        <Select
                            value={selectedRuleSet}
                            onChange={(e) => setSelectedRuleSet(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '150px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All RuleSets"
                        >
                            {ruleSetOptions.map((rs) => (
                                <option key={rs} value={rs}>{ruleSetLabel(rs)}</option>
                            ))}
                        </Select>
                        <Select
                            value={selectedTournament}
                            onChange={(e) => setSelectedTournament(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All tournaments"
                        >
                            {tournamentOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                        <Select
                            value={selectedTeamOne}
                            onChange={(e) => setSelectedTeamOne(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All (team 1)"
                        >
                            {teamOneOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                        <Select
                            value={selectedTeamTwo}
                            onChange={(e) => setSelectedTeamTwo(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All (team 2)"
                        >
                            {teamTwoOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                        <Flex 
                            direction={{ base: 'row', md: 'row' }} 
                            gap={2}
                            justify={{ base: 'flex-start', md: 'flex-start' }}
                            width={{ base: '100%', md: 'auto' }}
                        >
                            {selectedDate && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedDate('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Date
                                </Button>
                            )}
                            {selectedSport && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedSport('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Sport
                                </Button>
                            )}
                            {selectedRuleSet && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedRuleSet('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear RuleSet
                                </Button>
                            )}
                            {selectedTournament && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedTournament('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Tournament
                                </Button>
                            )}
                            {selectedTeamOne && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedTeamOne('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Team 1
                                </Button>
                            )}
                            {selectedTeamTwo && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedTeamTwo('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Team 2
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </Box>

                {isLoading ? (
                    <LoadingSpinner message="Loading events..." />
                ) : Object.keys(groupedEvents).length === 0 ? (
                    <EmptyState message="No events found for the selected date and filters." />
                ) : (
                    Object.entries(groupedEvents)
                        .sort(([dateA], [dateB]) => dateB.localeCompare(dateA)) // Sort in descending order
                        .map(([date, dateEvents]) => (
                            <EventsTable 
                                key={date} 
                                date={date} 
                                events={dateEvents} 
                                timeZone={timeZone}
                                onCancelEvent={cancelEvent} 
                            />
                        ))
                )}
            </VStack>
        </Box>
    );
};
